// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Training Library (ported from Firebase TrainingLibrary.jsx)
// Training catalog management, FEMA seed data, category/frequency filters
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  TextInput,
  Select,
  Button,
  Text,
  Group,
  Badge,
  Modal,
  Textarea,
  NumberInput,
  SimpleGrid,
  ActionIcon,
  Checkbox,
  Stack,
  Loader,
  Center,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconPlus,
  IconSearch,
  IconFolder,
  IconClock,
  IconBook,
  IconPencil,
  IconTrash,
  IconSeedling,
  IconBookOff,
} from '@tabler/icons-react';
import { useBlackboardStore } from '../../store/blackboardStore';
import { TRAINING_CATEGORIES, FEMA_SEED_TRAININGS } from './utils/femaTrainings';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Constants ────────────────────────────────────────────────

const FREQUENCY_OPTIONS = [
  { value: 'all', label: 'All Frequencies' },
  { value: '0', label: 'One-Time' },
  { value: '365', label: 'Annual' },
  { value: '730', label: 'Biennial' },
  { value: 'custom', label: 'Custom' },
];

const FREQUENCY_FORM_OPTIONS = [
  { value: '0', label: 'One-Time' },
  { value: '365', label: 'Annual' },
  { value: '730', label: 'Biennial' },
  { value: '1095', label: 'Triennial' },
  { value: 'custom', label: 'Custom' },
];

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'student', label: 'Student' },
];

const CATEGORY_COLORS: Record<string, string> = {
  'NIMS/ICS': 'var(--color-primary-700)',
  'Professional Development': 'var(--data-viz-650)',
  'Hazmat/Safety': '#F86A0B',
  'Health & Safety': 'var(--color-error)',
  'Operations': '#03AC9E',
  'Community Programs': 'var(--color-success)',
  'Weather': '#03AC9E',
  'Communications': 'var(--data-viz-100)',
  'Custom': '#939598',
};

function getFrequencyLabel(days: number): string {
  if (days === 0) return 'One-Time';
  if (days === 365) return 'Annual';
  if (days === 730) return 'Biennial';
  if (days === 1095) return 'Triennial';
  return `${days} days`;
}

function getFrequencyBadgeColor(days: number): string {
  if (days === 0) return 'gray';
  if (days === 365) return 'blue';
  if (days === 730) return 'teal';
  if (days === 1095) return 'violet';
  return 'orange';
}

// ── Main Component ───────────────────────────────────────────

export default function TrainingLibrary() {
  const { canEdit } = useModulePermission('blackboard');
  const { trainings, loading, loadTrainings, createTraining, updateTraining } = useBlackboardStore();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [frequencyFilter, setFrequencyFilter] = useState('all');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<(typeof trainings)[number] | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState<string | null>(null);
  const [formFrequency, setFormFrequency] = useState('0');
  const [formCustomDays, setFormCustomDays] = useState<number | string>(90);
  const [formDescription, setFormDescription] = useState('');
  const [formRoles, setFormRoles] = useState<string[]>([]);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<(typeof trainings)[number] | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Seed state
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    loadTrainings();
  }, [loadTrainings]);

  // Filter to only active trainings for display
  const activeTrainings = useMemo(() =>
    trainings.filter((t) => t.active === 'true' || t.active === '1' || t.active === 'yes'),
    [trainings]
  );

  const filtered = useMemo(() => {
    return activeTrainings.filter((t) => {
      if (search && !(t.name || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryFilter && t.category !== categoryFilter) return false;
      if (frequencyFilter !== 'all') {
        if (frequencyFilter === 'custom') {
          if ([0, 365, 730, 1095].includes(t.frequencyDays)) return false;
        } else if (t.frequencyDays !== parseInt(frequencyFilter, 10)) {
          return false;
        }
      }
      return true;
    });
  }, [activeTrainings, search, categoryFilter, frequencyFilter]);

  const resetForm = () => {
    setFormName('');
    setFormCategory(null);
    setFormFrequency('0');
    setFormCustomDays(90);
    setFormDescription('');
    setFormRoles([]);
    setEditing(null);
  };

  const openAdd = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (training: (typeof trainings)[number]) => {
    setEditing(training);
    setFormName(training.name);
    setFormCategory(training.category || null);
    setFormDescription(training.description || '');

    // Parse requiredForRoles — stored as comma-separated string
    const roles = training.requiredForRoles
      ? training.requiredForRoles.split(',').map((r) => r.trim()).filter(Boolean)
      : [];
    setFormRoles(roles);

    const days = training.frequencyDays;
    if ([0, 365, 730, 1095].includes(days)) {
      setFormFrequency(String(days));
      setFormCustomDays(90);
    } else {
      setFormFrequency('custom');
      setFormCustomDays(days);
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      notifications.show({ title: 'Validation', message: 'Training name is required.', color: '#C68700' });
      return;
    }
    setSaving(true);

    const frequencyDays =
      formFrequency === 'custom' ? (Number(formCustomDays) || 0) : parseInt(formFrequency, 10);

    const data = {
      name: formName.trim(),
      category: formCategory || 'Custom',
      frequencyDays,
      description: formDescription.trim(),
      requiredForRoles: formRoles.join(','),
      active: 'true',
    };

    try {
      if (editing) {
        await updateTraining(editing.id, data);
        notifications.show({ title: 'Success', message: 'Training updated.', color: 'var(--color-success)' });
      } else {
        await createTraining(data);
        notifications.show({ title: 'Success', message: 'Training created.', color: 'var(--color-success)' });
      }
      setModalOpen(false);
      resetForm();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to save training: ' + msg, color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // Soft delete — mark inactive
      await updateTraining(deleteTarget.id, { active: 'false' });
      notifications.show({ title: 'Removed', message: 'Training removed.', color: 'var(--color-success)' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to delete training: ' + msg, color: 'var(--color-error)' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      for (const t of FEMA_SEED_TRAININGS) {
        await createTraining({
          name: t.name,
          category: t.category,
          frequencyDays: t.frequencyDays,
          description: t.description,
          requiredForRoles: '',
          active: 'true',
        });
      }
      notifications.show({
        title: 'Seeded',
        message: `Seeded ${FEMA_SEED_TRAININGS.length} FEMA trainings.`,
        color: 'var(--color-success)',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to seed trainings: ' + msg, color: 'var(--color-error)' });
    } finally {
      setSeeding(false);
    }
  };

  const categoryOptions = TRAINING_CATEGORIES.map((c) => ({ value: c, label: c }));

  if (loading) {
    return (
      <Center h={400}>
        <Loader color="var(--color-primary-700)" />
      </Center>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Text size="10px" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: 2 }}>
            Administration
          </Text>
          <Text size="xl" fw={700} c="#1a365d">
            Training Library
          </Text>
        </Box>
        <Group gap="xs">
          {canEdit && activeTrainings.length < 5 && (
            <Button
              variant="outline"
              color="var(--color-primary-700)"
              leftSection={<IconSeedling size={14} />}
              onClick={handleSeed}
              loading={seeding}
            >
              Seed FEMA Trainings
            </Button>
          )}
          {canEdit && (
          <Button
            color="var(--color-primary-700)"
            leftSection={<IconPlus size={14} />}
            onClick={openAdd}
          >
            Add Training
          </Button>
          )}
        </Group>
      </Group>

      {/* Filters */}
      <Paper p="sm" mb="lg" withBorder>
        <Group grow>
          <TextInput
            placeholder="Search trainings..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="All Categories"
            data={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            leftSection={<IconFolder size={14} />}
          />
          <Select
            placeholder="All Frequencies"
            data={FREQUENCY_OPTIONS}
            value={frequencyFilter}
            onChange={(v) => setFrequencyFilter(v || 'all')}
            leftSection={<IconClock size={14} />}
          />
        </Group>
      </Paper>

      {/* Training cards */}
      {filtered.length === 0 ? (
        <Paper p="xl" withBorder>
          <Center>
            <Box ta="center">
              <IconBookOff size={32} color="#868e96" style={{ marginBottom: 8 }} />
              <Text c="dimmed" size="sm">
                No trainings found. Add a training or adjust your filters.
              </Text>
            </Box>
          </Center>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {filtered.map((t) => (
            <Paper
              key={t.id}
              p="md"
              withBorder
              style={{
                cursor: 'default',
                transition: 'transform 150ms ease, box-shadow 150ms ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <Group justify="space-between" align="flex-start" mb="xs">
                <Text fw={600} size="sm" style={{ flex: 1, paddingRight: 8 }}>
                  {t.name}
                </Text>
                <Badge size="sm" style={{ background: CATEGORY_COLORS[t.category] || '#939598', color: '#fff' }}>
                  {t.category || 'Uncategorized'}
                </Badge>
              </Group>

              <Text size="xs" c="dimmed" mb="sm" lineClamp={2} style={{ minHeight: 32 }}>
                {t.description || 'No description provided.'}
              </Text>

              {/* Required roles */}
              {t.requiredForRoles && (
                <Group gap={4} mb="xs">
                  {t.requiredForRoles.split(',').filter(Boolean).map((r) => (
                    <Badge key={r.trim()} size="xs" variant="dot" color="#939598">
                      {r.trim()}
                    </Badge>
                  ))}
                </Group>
              )}

              <Group justify="space-between" align="center">
                <Badge
                  color={getFrequencyBadgeColor(t.frequencyDays)}
                  size="sm"
                  variant="light"
                >
                  {getFrequencyLabel(t.frequencyDays)}
                </Badge>
                {canEdit && (
                <Group gap={4}>
                  <ActionIcon
                    variant="subtle"
                    color="var(--color-primary-700)"
                    size="sm"
                    onClick={() => openEdit(t)}
                    title="Edit"
                  >
                    <IconPencil size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="var(--color-error)"
                    size="sm"
                    onClick={() => setDeleteTarget(t)}
                    title="Delete"
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
                )}
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* Add/Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title={<Text fw={600}>{editing ? 'Edit Training' : 'Add Training'}</Text>}
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Training Name"
            placeholder="e.g. IS-100: Introduction to ICS"
            required
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            leftSection={<IconBook size={14} />}
          />

          <Select
            label="Category"
            placeholder="Select a category"
            data={categoryOptions}
            value={formCategory}
            onChange={setFormCategory}
            clearable
            leftSection={<IconFolder size={14} />}
          />

          <Group grow>
            <Select
              label="Frequency"
              data={FREQUENCY_FORM_OPTIONS}
              value={formFrequency}
              onChange={(v) => setFormFrequency(v || '0')}
              leftSection={<IconClock size={14} />}
            />
            {formFrequency === 'custom' && (
              <NumberInput
                label="Custom Days"
                placeholder="e.g. 180"
                min={1}
                max={9999}
                value={formCustomDays}
                onChange={setFormCustomDays}
              />
            )}
          </Group>

          <Textarea
            label="Description"
            placeholder="Brief description of this training..."
            minRows={3}
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
          />

          <Box>
            <Text size="sm" fw={500} mb={4}>
              Required For Roles
            </Text>
            <Checkbox.Group value={formRoles} onChange={setFormRoles}>
              <Group>
                {ROLE_OPTIONS.map((r) => (
                  <Checkbox key={r.value} value={r.value} label={r.label} size="sm" />
                ))}
              </Group>
            </Checkbox.Group>
          </Box>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => { setModalOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button color="var(--color-primary-700)" onClick={handleSave} loading={saving}>
              {editing ? 'Update Training' : 'Create Training'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={<Text fw={600}>Delete Training</Text>}
        size="sm"
      >
        <Text size="sm" mb="lg">
          Are you sure you want to remove{' '}
          <Text span fw={600}>{deleteTarget?.name}</Text>?
          This will deactivate the training.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button color="var(--color-error)" onClick={handleDelete} loading={deleting}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Box>
  );
}
